import { Outlet, Link, createRootRoute, HeadContent, Scripts } from "@tanstack/react-router";
import appCss from "../styles.css?url";
import { AuthProvider } from "@/hooks/useAuth";
import { ThemeProvider } from "@/hooks/useTheme";
import { Toaster } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState } from "react";
import { ScrollToTop } from "@/components/ScrollToTop";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "CoachQuick — The all-in-one CRM for sports coaches" },
      {
        name: "description",
        content:
          "CoachQuick helps coaches manage clients, log lessons, draft AI summaries, and share homework drills — for pickleball, tennis, golf and more.",
      },
      // Sitewide social defaults — overridden per-route via buildSocialMeta().
      { property: "og:site_name", content: "CoachQuick" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: "https://coachquick.com/coachquick-og.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "CoachQuick — The all-in-one CRM for sports coaches" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://coachquick.com/coachquick-og.jpg" },
      { name: "twitter:image:alt", content: "CoachQuick — The all-in-one CRM for sports coaches" },
      { name: "google-site-verification", content: "exWXqR33NpVNvy2IoxKExNbBz_fqtusburumCO9QtTg" },

      { property: "og:title", content: "CoachQuick — The all-in-one CRM for sports coaches" },
      { name: "twitter:title", content: "CoachQuick — The all-in-one CRM for sports coaches" },
      { property: "og:description", content: "CoachQuick helps coaches manage clients, log lessons, draft AI summaries, and share homework drills — for pickleball, tennis, golf and more." },
      { name: "twitter:description", content: "CoachQuick helps coaches manage clients, log lessons, draft AI summaries, and share homework drills — for pickleball, tennis, golf and more." },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "https://fonts.gstatic.com/s/inter/v20/UcC73FwrK3iLTeHuS_nVMrMxCp50SjIa1ZL7W0Q5nw.woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "https://fonts.gstatic.com/s/manrope/v20/xn7gYHE41ni1AdIRggexSvfedN4.woff2",
        crossOrigin: "anonymous",
      },
      // Google Fonts CSS with display=swap so the page paints with fallback
      // text immediately and swaps to the brand fonts once they load.
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Manrope:wght@400;500;600;700;800;900&display=swap",
      },


    ],

    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "CoachQuick",
          url: "https://coachquick.com",
          logo: "https://coachquick.com/coachquick-wordmark.png",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "CoachQuick",
          url: "https://coachquick.com",
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            refetchOnWindowFocus: false,
          },
        },
      })
  );
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <ScrollToTop />
          <Outlet />
          <Toaster />
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
